'use client';

/**
 * AppShell Context
 * Provides centralized state and actions for the layout system
 *
 * This context manages:
 * - Current route information
 * - Authentication state
 * - Sidebar state (open/collapsed)
 * - Agency branding for multi-tenant
 * - Feature flags
 */

import { createContext, useContext } from 'react';
import type {
  AppShellState,
  AppShellActions,
  AppShellContextValue,
  AppShellContextValueWithAuth,
  ResolvedRoute,
  LayoutVariant,
  AuthContext,
  AdminStatus,
  PermissionsState,
  AccessControlState,
} from '@/lib/routes/types';
import type { UserSettingsContext, AgencyBranding } from '@/lib/settings/types';
import { STAFF_ROLE_SLUGS, ADMIN_ROLE_SLUGS, TENANT_ANALYTICS_ROLE_SLUGS } from '@/lib/auth/role-slugs';
import { isFeatureAccessible } from '@/lib/features';

// ============================================================================
// Context Definition
// ============================================================================

// Use extended type with centralized auth state
const AppShellContext = createContext<AppShellContextValueWithAuth | null>(null);

type RoleLike = { slug?: string; name?: string };
type RoleContainer = { roles?: RoleLike[] };

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Access the AppShell context
 *
 * @throws Error if used outside AppShellProvider
 *
 * @example
 * const { currentRoute, user, toggleSidebar } = useAppShell();
 */
export function useAppShell(): AppShellContextValueWithAuth {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within AppShellProvider');
  }
  return context;
}

// ============================================================================
// Selector Hooks (for performance)
// ============================================================================

/**
 * Get current route information
 */
export function useCurrentRoute(): ResolvedRoute | null {
  const { currentRoute } = useAppShell();
  return currentRoute;
}

/**
 * Get authentication state
 */
export function useAuthState() {
  const { user, userContext, isAuthLoading } = useAppShell();
  return { user, userContext, isAuthLoading };
}

/**
 * Get sidebar state and actions
 */
export function useSidebarState() {
  const {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
  } = useAppShell();
  return {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
  };
}

/**
 * Get agency branding for multi-tenant customization
 */
export function useAgencyBranding(): AgencyBranding | null {
  return useAppShell().agencyBranding;
}

/**
 * Get feature flags (supports tri-state: disabled, warning, enabled)
 */
export function useFeatureFlags(): Record<string, import('@/lib/features').FeatureFlagState | boolean> {
  return useAppShell().featureFlags;
}

/**
 * Check if a feature is enabled (accessible)
 * Uses isFeatureAccessible to handle tri-state flags
 */
export function useFeatureEnabled(featureName: string): boolean {
  const flags = useFeatureFlags();
  return isFeatureAccessible(flags[featureName]);
}

/**
 * Get feature flag actions for managing preview features
 */
export function useFeatureFlagActions() {
  const { setFeatureFlag, setFeatureFlags, featureFlags } = useAppShell();
  return { setFeatureFlag, setFeatureFlags, featureFlags };
}

/**
 * Get current layout variant
 */
export function useLayoutVariant(): LayoutVariant {
  const { currentRoute, layoutOverride } = useAppShell();
  return layoutOverride || currentRoute?.layout || 'default';
}

// ============================================================================
// User Info Hooks
// ============================================================================

/**
 * Get current user info
 */
export function useUser(): AuthContext['user'] {
  return useAppShell().user;
}

/**
 * Check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { user, isAuthLoading } = useAppShell();
  return !isAuthLoading && !!user;
}

/**
 * Get user's role
 */
export function useUserRole(): string | undefined {
  const { user } = useAppShell();
  return user?.role;
}

/**
 * Get user's platform role
 */
export function useUserPlatformRole(): string | undefined {
  const { user, userContext } = useAppShell();
  return userContext?.platform_role || user?.platformRole || user?.app_metadata?.platform_role;
}

/**
 * Check if user is staff — DB-backed, no JWT fallback.
 * Checks platform_roles, agency roles, and department roles against
 * the canonical STAFF_ROLE_SLUGS set. Also considers is_platform_admin
 * flag (which can come from user_settings_permissions, not just role slugs).
 *
 * Returns false while access control state is loading to avoid flash —
 * consumers should gate on loading state if they need to distinguish
 * "not staff" from "not yet known".
 */
export function useIsStaff(): boolean {
  return useUserHoldsRoleIn(STAFF_ROLE_SLUGS);
}

/**
 * Check if user is admin — DB-backed, no JWT fallback.
 * Checks is_platform_admin, is_tenant_admin, and all role slugs against
 * the canonical ADMIN_ROLE_SLUGS set (including agency/department roles).
 */
export function useIsAdmin(): boolean {
  return useUserHoldsRoleIn(ADMIN_ROLE_SLUGS);
}

/**
 * May the user see tenant-wide call analytics (cost totals, raw LLM error text, per-officer
 * rankings)? Supervisors and admins only (Q-299 P0 #3). The database enforces this; pages use
 * it to render a "needs a supervisor" state instead of issuing a request that will be refused.
 * False while access control is loading, like the hooks above.
 */
export function useCanSeeTenantAnalytics(): boolean {
  return useUserHoldsRoleIn(TENANT_ANALYTICS_ROLE_SLUGS);
}

export function useIsSupervisorOrAdmin(): boolean {
  return useUserHoldsRoleIn(TENANT_ANALYTICS_ROLE_SLUGS);
}


/**
 * DB-backed role test shared by the hooks above: platform or tenant admin, or any platform,
 * agency or department role whose slug (or name) is in `slugs`.
 */
function useUserHoldsRoleIn(slugs: ReadonlySet<string>): boolean {
  const { accessControlState } = useAppShell();
  const uc = accessControlState?.userContext;
  if (!uc) return false;
  if (uc.is_platform_admin) return true;
  if (uc.is_tenant_admin) return true;

  const allRoles = [
    ...(uc.platform_roles || []),
    ...(uc.agencies || []).flatMap((a: RoleContainer) => a.roles || []),
    ...(uc.departments || []).flatMap((d: RoleContainer) => d.roles || []),
  ];
  return allRoles.some((r: RoleLike) =>
    (r.slug ? slugs.has(r.slug) : false) ||
    (r.name ? slugs.has(r.name) : false)
  );
}

/**
 * Check if user is citizen
 */
export function useIsCitizen(): boolean {
  const role = useUserRole();
  return role === 'citizen';
}

/**
 * Check if user is developer (API/integration user)
 */
export function useIsDeveloper(): boolean {
  const role = useUserRole();
  return role === 'developer';
}

/**
 * Check if user is anonymous (not authenticated)
 */
export function useIsAnonymous(): boolean {
  const { user, isAuthLoading } = useAppShell();
  return !isAuthLoading && !user;
}

// ============================================================================
// Centralized Auth Hooks (NEW - use these instead of creating auth listeners)
// ============================================================================

/**
 * Get auth version for triggering effects on auth changes
 * Use this in useEffect dependencies instead of creating auth listeners
 */
export function useAuthVersion(): number {
  return useAppShell().authVersion;
}

/**
 * Get last auth refresh timestamp (for coordination between components)
 */
export function useLastAuthRefresh(): number {
  return useAppShell().lastAuthRefresh;
}

/**
 * Safe version of useLastAuthRefresh that returns 0 if outside AppShellProvider context
 * Use this in components that may render both inside and outside the shell (e.g., call pages)
 */
export function useLastAuthRefreshSafe(): number {
  const context = useContext(AppShellContext);
  return context?.lastAuthRefresh ?? 0;
}

/**
 * Get centralized admin status (replaces useIsAdmin with its own listener)
 */
export function useAdminStatus(): AdminStatus {
  return useAppShell().adminStatus;
}

/**
 * Get centralized permissions state (replaces usePermissions listener)
 */
export function usePermissionsState(): PermissionsState {
  return useAppShell().permissionsState;
}

/**
 * Get centralized access control state (replaces useAccessControl listener)
 */
export function useAccessControlState(): AccessControlState {
  return useAppShell().accessControlState;
}

/**
 * Check if user has a specific permission (from centralized state)
 */
export function useHasPermission(permissionCode: string): boolean {
  const { permissionsState } = useAppShell();
  return permissionsState.permissions.includes(permissionCode);
}

/**
 * Check if user has any of the specified permissions
 */
export function useHasAnyPermission(permissionCodes: string[]): boolean {
  const { permissionsState } = useAppShell();
  if (!permissionCodes?.length) return false;
  return permissionCodes.some((code) => permissionsState.permissions.includes(code));
}

/**
 * Check if user has all of the specified permissions
 */
export function useHasAllPermissions(permissionCodes: string[]): boolean {
  const { permissionsState } = useAppShell();
  if (!permissionCodes?.length) return false;
  return permissionCodes.every((code) => permissionsState.permissions.includes(code));
}

// ============================================================================
// Optional (Safe) Hooks — do not throw when outside AppShellProvider
// ============================================================================

/**
 * Access the AppShell context without throwing.
 * Returns null if used outside AppShellProvider.
 */
export function useOptionalAppShell(): AppShellContextValueWithAuth | null {
  return useContext(AppShellContext);
}

/**
 * Get centralized permissions state, or null if outside AppShellProvider.
 */
export function useOptionalPermissionsState(): PermissionsState | null {
  return useOptionalAppShell()?.permissionsState ?? null;
}

/**
 * Get centralized access control state, or null if outside AppShellProvider.
 */
export function useOptionalAccessControlState(): AccessControlState | null {
  return useOptionalAppShell()?.accessControlState ?? null;
}

/**
 * Get centralized admin status, or null if outside AppShellProvider.
 */
export function useOptionalAdminStatus(): AdminStatus | null {
  return useOptionalAppShell()?.adminStatus ?? null;
}

// ============================================================================
// Export Context for Provider
// ============================================================================

export { AppShellContext };
export type { AppShellContextValue, AppShellContextValueWithAuth };
